import { useCallback, useEffect, useRef, useState } from "react";
import { DailyLineSettingPanel } from "./DailyLineSettingPanel";
import { DAILY_LINE_STATES, dailyLineState, dailyLineRequest } from "../dailyLineSetting.js";
import { t } from "../strings.js";

// IT #179 — **THE MORNING LINE'S DOOR.** The state and the two round trips; the panel beside it is
// hookless and is what the gate CALLS.
//
// **IT READS THE SERVER, NEVER A CACHED GUESS.** Unlike the notifications panel — which reads the
// BROWSER, because what it reports is whether this device actually receives — this preference lives
// on her account and travels with her, so a second device must not start over and a stale local copy
// must not tell her she is subscribed to something she turned off on her phone.
export function DailyLineSetting({ language = "en" }: { language?: string }) {
  // **NULL IS "NOT LOOKED YET", NEVER "OFF"** (P4-1's ruling one preference over): a control that
  // draws OFF while it is still reading tells a member she has unsubscribed from something she is
  // still receiving, and invites her to repair what is not broken.
  const [answer, setAnswer] = useState<null | { on: boolean }>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const read = useCallback(async () => {
    const got = await fetch("/api/daily-line/setting", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!alive.current) return;
    // A read that failed leaves the state UNKNOWN rather than inventing one, so the panel draws no
    // button — a control that cannot know what it would do must not offer to do it.
    if (got && typeof got.on === "boolean") setAnswer({ on: got.on });
  }, []);

  useEffect(() => { void read(); }, [read]);

  const act = useCallback(async (action: string) => {
    const body = dailyLineRequest(action);
    if (!body) return;
    setBusy(true); setNote("");
    const got = await fetch("/api/daily-line/setting", {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!alive.current) return;
    setBusy(false);
    // R47/R53/R61: the answer is READ. A control that reports success on a refused write is the one
    // shape a member never reports, because her screen looked right.
    if (got && typeof got.on === "boolean") setAnswer({ on: got.on });
    else setNote(t(language, "daily.setting.failed"));
  }, [language]);

  const state = answer ? dailyLineState(answer) : DAILY_LINE_STATES.UNKNOWN;
  return <DailyLineSettingPanel state={state} busy={busy} note={note} language={language} onAct={act} />;
}
